import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type UIEvent,
} from "react";
import { useHistoryArchive } from "../hooks/useHistoryArchive";
import {
  historyAriaRowCount,
  historyAriaRowIndex,
  parseHistoryIndexInput,
} from "../lib/history";
import { formatDecimal, operationSymbol } from "../lib/format";
import type { WorldTerm } from "../lib/world";

const DESKTOP_ROW_HEIGHT = 42;
const MOBILE_ROW_HEIGHT = 64;
const EDGE_LOAD_ROWS = 8;

interface HistoryArchiveProps {
  liveTerm: WorldTerm;
  liveTerms: readonly WorldTerm[];
  isMobile: boolean;
}

interface VisibleAnchor {
  n: string;
  viewportOffset: number;
}

export function HistoryArchive({ liveTerm, liveTerms, isMobile }: HistoryArchiveProps) {
  const archive = useHistoryArchive(liveTerm, liveTerms);
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<VisibleAnchor | null>(null);
  const pendingJumpRef = useRef<string | null>(null);
  const [jumpValue, setJumpValue] = useState("");
  const [jumpError, setJumpError] = useState("");
  const rowHeight = isMobile ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT;

  const getItemKey = useCallback(
    (index: number) => archive.terms[index]?.n ?? `history-row-${index}`,
    [archive.terms],
  );
  // TanStack Virtual intentionally exposes a mutable controller; React Compiler skips this component.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: archive.terms.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    getItemKey,
    overscan: 8,
  });

  const captureVisibleAnchor = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const firstVisible = rowVirtualizer
      .getVirtualItems()
      .find((item) => item.end > scroller.scrollTop);
    const term = firstVisible ? archive.terms[firstVisible.index] : undefined;
    if (!firstVisible || !term) return;
    anchorRef.current = {
      n: term.n,
      viewportOffset: firstVisible.start - scroller.scrollTop,
    };
  }, [archive.terms, rowVirtualizer]);

  useLayoutEffect(() => {
    const pendingJump = pendingJumpRef.current;
    if (pendingJump !== null) {
      const index = archive.terms.findIndex((term) => term.n === pendingJump);
      if (index >= 0) {
        rowVirtualizer.scrollToIndex(index, {
          align: pendingJump === archive.through.toString() ? "start" : "center",
        });
        pendingJumpRef.current = null;
        anchorRef.current = null;
      }
      return;
    }

    if (archive.followingLatest) {
      rowVirtualizer.scrollToOffset(0);
      anchorRef.current = null;
      return;
    }

    const anchor = anchorRef.current;
    if (!anchor) return;
    const nextIndex = archive.terms.findIndex((term) => term.n === anchor.n);
    if (nextIndex < 0) return;
    rowVirtualizer.scrollToOffset(Math.max(0, nextIndex * rowHeight - anchor.viewportOffset));
  }, [archive.followingLatest, archive.terms, archive.through, rowHeight, rowVirtualizer]);

  const loadAtEdges = (scroller: HTMLDivElement) => {
    if (archive.isBusy) return;
    const edgeDistance = rowHeight * EDGE_LOAD_ROWS;
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (distanceFromBottom < edgeDistance && archive.hasOlder) {
      captureVisibleAnchor();
      void archive.loadOlder();
    } else if (scroller.scrollTop < edgeDistance && archive.hasNewer) {
      captureVisibleAnchor();
      void archive.loadNewer();
    }
  };

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const scroller = event.currentTarget;
    captureVisibleAnchor();
    if (scroller.scrollTop > rowHeight / 2) archive.stopFollowing();
    loadAtEdges(scroller);
  };

  const jump = async (n: bigint) => {
    pendingJumpRef.current = n.toString();
    setJumpError("");
    await archive.jumpTo(n);
  };

  const onJumpSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = parseHistoryIndexInput(jumpValue);
    const latest = BigInt(liveTerm.n);
    if (target === null || target > latest) {
      setJumpError(`0〜${formatDecimal(liveTerm.n)}の項番号を入力してください。`);
      return;
    }
    void jump(target);
  };

  const goFirst = () => {
    pendingJumpRef.current = "0";
    setJumpError("");
    void archive.goFirst();
  };

  const goLatest = () => {
    const latest = liveTerm.n;
    pendingJumpRef.current = latest;
    setJumpError("");
    void archive.goLatest();
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const scroller = event.currentTarget;
    let nextTop: number | null = null;
    if (event.key === "ArrowUp") nextTop = scroller.scrollTop - rowHeight;
    if (event.key === "ArrowDown") nextTop = scroller.scrollTop + rowHeight;
    if (event.key === "PageUp") nextTop = scroller.scrollTop - scroller.clientHeight * 0.9;
    if (event.key === "PageDown") nextTop = scroller.scrollTop + scroller.clientHeight * 0.9;
    if (event.key === "Home") {
      event.preventDefault();
      goLatest();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      goFirst();
      return;
    }
    if (nextTop === null) return;
    event.preventDefault();
    scroller.scrollTop = Math.max(0, nextTop);
  };

  const newest = archive.terms[0];
  const oldest = archive.terms.at(-1);
  const rangeLabel = newest && oldest
    ? `n ${formatDecimal(oldest.n)}–${formatDecimal(newest.n)} / ${formatDecimal(archive.through.toString())}`
    : `n — / ${formatDecimal(archive.through.toString())}`;
  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <section className="history-archive" aria-labelledby="all-history-title">
      <div className="history-archive__heading">
        <div>
          <h3 id="all-history-title">ALL HISTORY</h3>
          <p className="history-archive__range" aria-live="polite">{rangeLabel}</p>
        </div>
        <span className={archive.followingLatest ? "history-archive__mode is-live" : "history-archive__mode"}>
          {archive.followingLatest ? "● LIVE" : "BROWSING"}
        </span>
      </div>

      <div className="history-archive__toolbar" aria-label="履歴の移動">
        <form className="history-jump" onSubmit={onJumpSubmit}>
          <label htmlFor="history-jump-input">n =</label>
          <input
            id="history-jump-input"
            value={jumpValue}
            onChange={(event) => setJumpValue(event.target.value)}
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={jumpError ? true : undefined}
            aria-describedby={jumpError ? "history-jump-error" : undefined}
            placeholder="0"
          />
          <button type="submit">GO</button>
        </form>
        <div className="history-archive__edge-actions">
          <button type="button" onClick={goFirst} aria-keyshortcuts="End">FIRST</button>
          <button
            type="button"
            onClick={goLatest}
            aria-keyshortcuts="Home"
            aria-pressed={archive.followingLatest}
          >
            LATEST
          </button>
        </div>
      </div>

      {jumpError ? <p id="history-jump-error" className="history-archive__error" role="alert">{jumpError}</p> : null}
      {archive.newCount > 0n ? (
        <button type="button" className="history-archive__new" onClick={goLatest}>
          +{formatDecimal(archive.newCount.toString())} NEW&nbsp; / &nbsp;現在へ
        </button>
      ) : null}
      {archive.error ? (
        <div className="history-archive__load-error" role="alert">
          <span>{archive.error}</span>
          <button type="button" onClick={() => void archive.retry()}>RETRY</button>
        </div>
      ) : null}
      <p id="history-keyboard-help" className="sr-only">
        上下矢印とPage Up、Page Downで移動できます。Homeで最新、Endで最初の項へ移動します。
      </p>

      <div
        ref={scrollRef}
        className="history-archive__scroll"
        role="region"
        aria-label="レカマン数列の全履歴"
        aria-describedby="history-keyboard-help"
        tabIndex={0}
        onScroll={onScroll}
        onKeyDown={onListKeyDown}
        data-page-count={archive.loadedPageCount}
      >
        <div
          className="history-archive__table"
          role="table"
          aria-label="レカマン数列の全履歴、最新の項から順"
          aria-rowcount={historyAriaRowCount(archive.through)}
          aria-colcount={4}
          aria-busy={archive.isBusy}
        >
          <div className="history-archive__header" role="row" aria-rowindex={1}>
            <span role="columnheader">項 n</span>
            <span role="columnheader">前の値</span>
            <span role="columnheader">規則</span>
            <span role="columnheader">値 a(n)</span>
          </div>
          <div
            className="history-archive__rows"
            role="rowgroup"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {virtualRows.map((virtualRow) => {
              const term = archive.terms[virtualRow.index];
              if (!term) return null;
              const isSeed = term.n === "0";
              return (
                <div
                  key={virtualRow.key}
                  className="history-archive__row"
                  role="row"
                  aria-rowindex={historyAriaRowIndex(archive.through, BigInt(term.n))}
                  data-index={virtualRow.index}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <span className="history-cell history-cell--index" role="cell">
                    <span className="history-cell__mobile-label" aria-hidden="true">項 n</span>
                    {formatDecimal(term.n)}
                  </span>
                  <span className="history-cell history-cell--previous" role="cell">
                    <span className="history-cell__mobile-label" aria-hidden="true">前</span>
                    {formatDecimal(term.previous)}
                  </span>
                  <span
                    className={isSeed ? "history-cell history-cell--operation is-seed" : `history-cell history-cell--operation is-${term.operation}`}
                    role="cell"
                  >
                    <span className="history-cell__mobile-label" aria-hidden="true">規則</span>
                    {isSeed ? "START" : `${operationSymbol(term.operation)} ${formatDecimal(term.step)}`}
                  </span>
                  <span className="history-cell history-cell--value" role="cell">
                    <span className="history-cell__mobile-label" aria-hidden="true">a(n)</span>
                    {formatDecimal(term.value)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {archive.isInitialLoading ? <p className="history-archive__loading" role="status">LOADING 512 TERMS…</p> : null}
      </div>
    </section>
  );
}
